import { EllipsedText, isValidLatitude, isValidLongitude, MaterialIcon, useMoment } from '@gorila-shared-ui/components';
import { StyledLink } from 'baseui/link';
import { StyledCell, StyledRow } from 'baseui/table';
import { CSSProperties, memo } from 'react';
import { areEqual, ListChildComponentProps } from 'react-window';
import { useRecoilState, useSetRecoilState } from 'recoil';
import {
  ROUTE_ADDRESS_COLUMN_SIZE,
  ROUTE_DATE_COLUMN_MAX_WIDTH,
  ROUTE_DATE_COLUMN_SIZE,
  ROUTE_EVENT_COLUMN_SIZE,
  ROUTE_IDENT_COLUMN_SIZE,
  ROUTE_LABEL_COLUMN_SIZE,
  ROUTE_SPEED_COLUMN_MAX_WIDTH,
  ROUTE_SPEED_COLUMN_SIZE,
  ROUTE_STATUS_COLUMN_MAX_WIDTH,
  ROUTE_STATUS_COLUMN_SIZE,
} from '../../../constants/reports';
import { useCamera } from '../../../hooks/useCamera';
import { useStyles } from '../../../hooks/useStyles';
import { temporaryPositionMarkerState } from '../../../storage/map';
import { selectedReportRowState } from '../../../storage/reports';
import { HistoricState } from '../../../types/currentState';
import { isDevice } from '../../../utils/currentStates';
import { StyledTooltip } from '../../ui/StyledTooltip';
import RouteReportNotificationType from './RouteReportNotificationType';

function RouteReportRow({
  data,
  index,
  style,
}: Readonly<
  ListChildComponentProps<{
    states: HistoricState[];
    extended?: boolean;
  }>
>) {
  const state = data.states[index];
  const extended = data.extended;
  const [selected, setSelected] = useRecoilState(selectedReportRowState);
  const { theme, css, styles } = useStyles();
  const { formatDateFromTs } = useMoment();
  const { zoomTo } = useCamera();
  const setTempPosition = useSetRecoilState(temporaryPositionMarkerState);

  const onClick = () => {
    const { position_latitude: lat, position_longitude: lng } = state;
    setSelected(index);
    if (lat && lng && isValidLatitude(lat) && isValidLongitude(lng)) {
      zoomTo({ lat, lng }, 18);
      if (extended) {
        setTempPosition({ address: '', point: { lat, lng } });
      }
    }
  };

  return (
    <StyledRow
      key={index}
      style={{ ...(style as CSSProperties), cursor: 'pointer' }}
      className={css({
        backgroundColor:
          selected === index
            ? theme.colors.gray100 // Active color
            : (index + 1) % 2 === 0
              ? theme.colors.tableStripedBackground
              : '',
        ':hover': {
          background: theme.colors.buttonTertiaryHover,
        },
      })}
      onClick={onClick}
    >
      {extended && (
        <StyledCell style={{ flex: ROUTE_IDENT_COLUMN_SIZE, ...styles.smallTableCell }}>
          {isDevice(state.asset.device) ? state.asset.device.ident : '-'}
        </StyledCell>
      )}
      {extended && (
        <StyledCell style={{ flex: ROUTE_LABEL_COLUMN_SIZE, ...styles.smallTableCell }}>
          {state.asset.label || '-'}
        </StyledCell>
      )}
      <StyledCell
        style={{ flex: ROUTE_DATE_COLUMN_SIZE, maxWidth: ROUTE_DATE_COLUMN_MAX_WIDTH, ...styles.smallTableCell }}
      >
        {formatDateFromTs(+state.timestamp)}
      </StyledCell>
      <StyledCell
        style={{
          justifyContent: 'center',
          flex: ROUTE_STATUS_COLUMN_SIZE,
          maxWidth: ROUTE_STATUS_COLUMN_MAX_WIDTH,
          ...styles.smallTableCell,
        }}
      >
        <StyledTooltip
          content={state.engine_ignition_status ? 'Encendido' : 'Apagado'}
          showArrow={false}
        >
          <MaterialIcon
            name="power_settings_new"
            color={state.engine_ignition_status ? theme.colors.contentPositive : theme.colors.contentNegative}
          />
        </StyledTooltip>
      </StyledCell>
      <StyledCell style={{ justifyContent: 'center', flex: ROUTE_EVENT_COLUMN_SIZE, ...styles.smallTableCell }}>
        <RouteReportNotificationType notificationId={state.notificationType} />
      </StyledCell>
      <StyledCell
        style={{
          justifyContent: 'center',
          flex: ROUTE_SPEED_COLUMN_SIZE,
          maxWidth: ROUTE_SPEED_COLUMN_MAX_WIDTH,
          whiteSpace: 'nowrap',
          ...styles.smallTableCell,
        }}
      >
        {state.position_speed} Km/h
      </StyledCell>
      <StyledCell style={{ flex: ROUTE_ADDRESS_COLUMN_SIZE, ...styles.smallTableCell }}>
        <StyledLink
          href={`https://maps.google.com/?q=${state.position_latitude},${state.position_longitude}`}
          target="_blank"
          style={{ ...theme.typography.ParagraphXSmall }}
        >
          <EllipsedText
            inline
            breakAll
          >
            {state.geoReference}
          </EllipsedText>
        </StyledLink>
      </StyledCell>
    </StyledRow>
  );
}

export default memo(RouteReportRow, areEqual);
